import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { tap, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import {CommunicationService} from "./services/communication.service";
import {CommunicationTerms} from "./models/communication-terms";
import {MatDialogRef} from "@angular/material/dialog";
import {getCurrentUser} from "aws-amplify/auth";
import {ManagementService} from "./services/management.service";

@Injectable({
  providedIn: 'root'
})
export class AuthGuard  {

  isLoggedIn: boolean = false;
  dialogRef: MatDialogRef<any> = null;
  constructor(
      private auth: AuthService,
      private router: Router,
      private communicationService: CommunicationService,
      private managementService: ManagementService
  ) { }

  async canActivate(
      next: ActivatedRouteSnapshot,
      state: RouterStateSnapshot
  ): Promise<boolean> {

    this.isLoggedIn = await this.auth.isLoggedIn();

    if((state?.url.includes('order-manager/order') || (state?.url.includes('lab-manager/submission') && !state?.url.includes('lab-manager/submissions'))) && !localStorage.getItem('firstTimeLogin')) {
      localStorage.setItem('firstTimeLogin', state?.url);
    }

    if (!this.isLoggedIn) {
      this.router.navigate(['']);
      return false;
    }

    // Ensure user details are available (uses cache, no API calls if already loaded)
    await this.auth.ensureUserDetails();
    this.callTermsAndConditions(next);
    return this.checkAuthenticationDetails(next);
  }

  callTermsAndConditions(next: ActivatedRouteSnapshot,) {
    this.communicationService.getCommunicationTerms().subscribe({
      next: (res: CommunicationTerms) => {
        if(res.confirmed) {
          return this.checkAuthenticationDetails(next);
        } else {
          if(!this.dialogRef) {
            this.dialogRef = this.communicationService.openTermsDialog(res.terms);

            this.dialogRef.afterClosed().subscribe({
              next: (res) => {
                if(res) {
                  const termIds = res.map(r => {
                    if(r.accepted) {
                      return r.id;
                    }
                  })
                  this.communicationService.setCommunicationTerms(termIds).subscribe({
                    next: () => {
                      return this.checkAuthenticationDetails(next);
                    }
                  });
                } else {
                  this.auth.logout(true);
                }
              }
            })
          }

          return this.checkAuthenticationDetails(next);
        }
      },
      error: (error) => {
        console.log(error);
      }
    });
  }

  checkAuthenticationDetails(next: ActivatedRouteSnapshot,) {
    if (this.router.url.includes('redirect')) {
      sessionStorage.setItem("orderApprovalRedirectLink", this.router.url.substring(0,this.router.url.length - 9));
    }
    let hasAccess = false;
    // If a user is authenticated and want to be redirected to the mapping page. (Only when a user opens the page for a new session.)
    if (next.routeConfig.path === 'mapping' && this.auth.loggedIn) {
      hasAccess = true;
    }

    next.data.shouldHaveRoles.forEach(shouldHaveRole => {
      if (['AngularField', 'AngularProfile', 'AngularPinManager'].includes(shouldHaveRole)) {
        hasAccess = true;
      }
    });

    // Setting/Getting the user roles available.
    let userRoleList = sessionStorage.getItem("roles") != null ? JSON.parse(sessionStorage.getItem('roles')) : null;
    if (!userRoleList) {
      // Synchronously grab current value from BehaviorSubject
      this.auth.roles$.pipe(take(1)).subscribe(results => {
        userRoleList = results;
      });
    }

    // Check roles in all cases
    if (userRoleList) {
      userRoleList.forEach(userRole => {
        next.data.shouldHaveRoles.forEach(shouldHaveRole => {
          if (shouldHaveRole === userRole.name) {
            hasAccess = true;
          }
        });
      });
    }

    // If no roles are found, redirect to mapping page.
    if (!hasAccess) {
      this.router.navigate(['mapping']);
    }

    return hasAccess;
  }
}